import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type NodeMouseHandler,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { layoutGraph, type FlowGroupData, type FlowNode, type FlowNodeData } from "./layout";
import { KIND_NAMES, nodeText, type GraphNode, type GraphOverlay, type TriggerGraph } from "./types";
import "./graph.css";

export interface TriggerGraphViewProps {
  graph: TriggerGraph | null;
  /** Node highlighted from the editor cursor (cursor-to-node). */
  selectedNodeId?: string | null;
  /** Click a node -> jump to its source (click-to-code). */
  onNodeClick?: (node: GraphNode) => void;
  onNodeHover?: (node: GraphNode | null) => void;
  /** Values per node id for the current second (trace overlay). */
  overlay?: GraphOverlay;
  /** Hide whole kinds (e.g. "custom") to declutter. */
  hiddenKinds?: Set<string>;
  collapsedPhases?: Set<string>;
  className?: string;
  /** Hide the minimap (narrow side panels). */
  minimap?: boolean;
  /** Show plain-language node text instead of the code label (default true). */
  plain?: boolean;
}

const EDGE_WORDS: Record<string, string> = {
  then: "yes →",
  else: "no →",
  elif: "no →",
  body: "each →",
  except: "on error →",
  finally: "always →",
};

export const KIND_ICON: Record<string, string> = {
  trigger: "⚡",
  report: "📄",
  param: "〜",
  input: "⚙",
  context: "◔",
  state: "▣",
  dependency: "⇢",
  compare: "≷",
  boolean: "∧",
  temporal: "⏱",
  expr: "ƒ",
  branch: "◇",
  loop: "↻",
  snapshot: "📷",
  control: "■",
  event: "✉",
  custom: "{ }",
};

function overlayText(v: boolean | number | string | null | undefined): string | null {
  if (v === undefined) return null;
  if (v === null) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
  return String(v);
}

function nodeTooltip(n: GraphNode, plain: boolean): string {
  const lines: string[] = [];
  if (plain && n.text) lines.push(n.text);
  lines.push(`${KIND_NAMES[n.kind] ?? n.kind} · ${n.label}`);
  if (n.source && n.source !== n.label) lines.push(n.source);
  return lines.join("\n");
}

function RsNode({ data, selected }: NodeProps<FlowNode & { data: FlowNodeData }>) {
  const n = data.node;
  const extra = data as FlowNodeData & { overlay?: boolean | number | string | null; plain?: boolean };
  const plain = extra.plain ?? true;
  const ov = extra.overlay;
  const ovText = overlayText(ov);
  const state = ov === true ? "true" : ov === false ? "false" : ov === null ? "nan" : "";
  const shown = nodeText(n, plain);
  return (
    <div
      className={`rs-node rs-kind-${n.kind}${selected ? " rs-selected" : ""}${state ? ` rs-ov-${state}` : ""}${n.traced ? " rs-traced" : ""}${plain && n.text ? " rs-plain" : ""}`}
      title={nodeTooltip(n, plain)}
    >
      <Handle type="target" position={Position.Left} className="rs-handle" />
      <span className="rs-icon">{KIND_ICON[n.kind] ?? "•"}</span>
      <span className="rs-label">{shown}</span>
      {n.badge ? <span className="rs-badge">{n.badge}</span> : null}
      {ovText !== null ? <span className="rs-overlay">{ovText}</span> : null}
      <Handle type="source" position={Position.Right} className="rs-handle" />
    </div>
  );
}

function RsGroup({ data }: NodeProps<FlowNode & { data: FlowGroupData }>) {
  return (
    <div className={`rs-group rs-phase-${data.phase ?? "none"}`}>
      <div className="rs-group-title">{data.title}</div>
    </div>
  );
}

const nodeTypes = { rsNode: RsNode, rsGroup: RsGroup };

function Inner({
  graph,
  selectedNodeId,
  onNodeClick,
  onNodeHover,
  overlay,
  hiddenKinds,
  collapsedPhases,
  minimap,
  plain = true,
}: TriggerGraphViewProps) {
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [laying, setLaying] = useState(false);
  const rf = useReactFlow();
  const lastFit = useRef<string>("");

  const layoutKey = useMemo(
    () =>
      graph
        ? JSON.stringify({
            f: graph.file,
            n: graph.nodes.map((n) => [n.id, n.label, n.parent]),
            e: graph.edges.map((e) => e.id),
            h: [...(hiddenKinds ?? [])],
            c: [...(collapsedPhases ?? [])],
            p: plain,
          })
        : "",
    [graph, hiddenKinds, collapsedPhases, plain],
  );

  useEffect(() => {
    if (!graph) {
      setNodes([]);
      setEdges([]);
      return;
    }
    let cancelled = false;
    setLaying(true);
    layoutGraph(graph, { hiddenKinds, collapsedPhases, plain })
      .then((res) => {
        if (cancelled) return;
        setNodes(res.nodes);
        setEdges(
          plain
            ? res.edges.map((e) => (typeof e.label === "string" && EDGE_WORDS[e.label] ? { ...e, label: EDGE_WORDS[e.label] } : e))
            : res.edges,
        );
        setLaying(false);
        if (lastFit.current !== layoutKey) {
          lastFit.current = layoutKey;
          requestAnimationFrame(() => rf.fitView({ padding: 0.15, duration: 200 }));
        }
      })
      .catch(() => setLaying(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey]);

  // selection + overlay are applied without re-layout
  const decorated = useMemo(
    () =>
      nodes.map((n) => {
        if (n.type !== "rsNode") return n;
        const data = n.data as FlowNodeData;
        const ov = overlay ? overlay[n.id] : undefined;
        return { ...n, selected: n.id === selectedNodeId, data: { ...data, overlay: ov, plain } };
      }),
    [nodes, selectedNodeId, overlay, plain],
  );

  const decoratedEdges = useMemo(() => {
    if (!selectedNodeId) return edges;
    return edges.map((e) =>
      e.source === selectedNodeId || e.target === selectedNodeId
        ? { ...e, className: `${e.className ?? ""} rs-edge-active` }
        : e,
    );
  }, [edges, selectedNodeId]);

  useEffect(() => {
    if (!selectedNodeId) return;
    const node = nodes.find((n) => n.id === selectedNodeId);
    if (!node) return;
    const w = (node.style?.width as number) ?? 160;
    const h = (node.style?.height as number) ?? 40;
    rf.setCenter(node.position.x + w / 2, node.position.y + h / 2, { zoom: Math.max(rf.getZoom(), 0.9), duration: 250 });
  }, [selectedNodeId, nodes, rf]);

  const handleClick = useCallback<NodeMouseHandler>(
    (_evt, node) => {
      if (node.type !== "rsNode") return;
      onNodeClick?.((node.data as FlowNodeData).node);
    },
    [onNodeClick],
  );
  const handleEnter = useCallback<NodeMouseHandler>(
    (_evt, node) => {
      if (node.type === "rsNode") onNodeHover?.((node.data as FlowNodeData).node);
    },
    [onNodeHover],
  );
  const handleLeave = useCallback(() => onNodeHover?.(null), [onNodeHover]);

  return (
    <div className="rs-graph">
      {graph?.errors?.length ? (
        <div className="rs-graph-banner rs-error">
          {graph.errors.map((e, i) => (
            <div key={i}>
              line {e.line}: {e.message}
            </div>
          ))}
        </div>
      ) : null}
      {graph && !graph.errors?.length && graph.warnings.length ? (
        <div className="rs-graph-banner rs-warning">{graph.warnings.join(" · ")}</div>
      ) : null}
      {laying ? <div className="rs-graph-status">layout…</div> : null}
      <ReactFlow
        nodes={decorated}
        edges={decoratedEdges}
        nodeTypes={nodeTypes}
        onNodeClick={handleClick}
        onNodeMouseEnter={handleEnter}
        onNodeMouseLeave={handleLeave}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        panOnScroll
        zoomOnDoubleClick={false}
        minZoom={0.2}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        fitView
      >
        <Background gap={18} size={1} />
        <Controls showInteractive={false} />
        {minimap !== false ? (
          <MiniMap
            pannable
            zoomable
            className="rs-minimap"
            style={{ width: 160, height: 100 }}
            nodeColor={(n) => (n.type === "rsGroup" ? "var(--rs-minimap-group)" : n.selected ? "var(--rs-accent)" : "var(--rs-minimap-node)")}
            nodeStrokeColor={(n) => (n.type === "rsGroup" ? "var(--rs-minimap-group-stroke)" : "transparent")}
            nodeStrokeWidth={4}
            nodeBorderRadius={6}
          />
        ) : null}
      </ReactFlow>
    </div>
  );
}

export function TriggerGraphView(props: TriggerGraphViewProps) {
  return (
    <div className={props.className ?? "rs-graph-host"}>
      <ReactFlowProvider>
        <Inner {...props} />
      </ReactFlowProvider>
    </div>
  );
}
